
// ---------------- IndexedDB Helpers ----------------
export function openDB(name, version, upgradeCallback) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, version);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (upgradeCallback) upgradeCallback(db);
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
}

export function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getTableData(tableName) {
  const db = await openDB("CompanyTables", 2);
  if (!db.objectStoreNames.contains(tableName)) {
    console.error(`Store '${tableName}' not found.`);
    return null;
  }

  const tx = db.transaction(tableName, "readonly");
  const store = tx.objectStore(tableName);
  const data = await requestToPromise(store.get("data"));
  if (!data || !Array.isArray(data.value)) return null;

  const rows = [];
  const columns = data.value.map(row => row[0]);
  for (let i = 1; i < data.value[0].length; i++) {
    const rowObj = {};
    for (let j = 0; j < columns.length; j++) {
      rowObj[columns[j]] = data.value[j][i] || 0;
    }
    rows.push(rowObj);
  }
  return rows;
}

export async function saveAllTables(tables) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(`CompanyTables`);

    request.onsuccess = (event) => {
      const db = event.target.result;
      const missingStores = tables
        .map(t => t.tableName)
        .filter(name => !db.objectStoreNames.contains(name));

      if (missingStores.length > 0) {
        const newVersion = db.version + 1;
        db.close();
        const upgradeRequest = indexedDB.open(`CompanyTables`, newVersion);

        upgradeRequest.onupgradeneeded = (e) => {
          const dbUpgrade = e.target.result;
          missingStores.forEach(storeName => dbUpgrade.createObjectStore(storeName, { keyPath: "key" }));
        };

        upgradeRequest.onsuccess = (e) => {
          const dbNew = e.target.result;
          tables.forEach(table => {
            const tx = dbNew.transaction(table.tableName, "readwrite");
            tx.objectStore(table.tableName).put({ key: "data", value: table.tableData });
          });
          resolve();
        };

        upgradeRequest.onerror = (e) => reject(e.target.error);
      } else {
        tables.forEach(table => {
          const tx = db.transaction(table.tableName, "readwrite");
          tx.objectStore(table.tableName).put({ key: "data", value: table.tableData });
        });
        resolve();
      }
    };

    request.onerror = (e) => reject(e.target.error);
  });
}

export function Average(arr) {
  if (!arr || arr.length === 0) return 0;
  const sum = arr.reduce((acc, val) => acc + val, 0);
  return sum / arr.length || 0;
}

export function formatCurrency(value) {
  if (value === "Date" || value === undefined) return value;
  return value < 0 ? `(${value.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })})` : value.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

const n = (val) => Number(val) || 0;

export function setBool(key, value) {
  localStorage.setItem(key, value);
}

export function getVal(key) {
  return JSON.parse(localStorage.getItem(key)); // Convert "true"/"false" strings to boolean
}

export function toggleBool(key) {
  const current = getVal(key);
  setBool(key, !current);
  return !current;
}

export function isNegative(val) {
  if (typeof val === "number") return val < 0;
  if (typeof val === "string") {
    // remove % and commas, parse as float
    const num = parseFloat(val.replace(/[%,$]/g, ""));
    return !isNaN(num) && num < 0;
  }
  return false;
}

export function formatDate(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);
  // "Sep-10"
  return date.toLocaleDateString("en-US", {
    month: "short",  // Jan, Feb, Mar...
    year: "2-digit", // 10, 11, 12...
  }).replace(" ", "-");
}

// ---------------- Derived Financial Calculations ----------------
function getRowValue(incomeStatement3M, incomeStatement12M) {
  if (!Array.isArray(incomeStatement3M) || !Array.isArray(incomeStatement12M)) {
    throw new Error("Both inputs must be arrays");
  }

  const metricNames = Object.keys(incomeStatement3M[0]).filter(
    k => !['AsOfDate','CompanyID','ImportDate'].includes(k)
  );

  // Tracks last used values (3M or 12M) per metric
  const history = {};

  return incomeStatement3M.map(IS3M => {
    const resolved = {};
    const date3M = new Date(IS3M['AsOfDate']);

    metricNames.forEach(metric => {
      let value3M = n(IS3M[metric]) || 0;

      if (value3M !== 0) {
        // Use 3M value as-is
        resolved[metric] = value3M;

        // Update history
        if (!history[metric]) history[metric] = [];
        history[metric].push(value3M);
        if (history[metric].length > 3) history[metric].shift();

        return;
      }

      // Fallback to latest 12M row before this 3M date
      let value12M = 0;
      for (let i = incomeStatement12M.length - 1; i >= 0; i--) {
        const row12M = incomeStatement12M[i];
        const date12M = new Date(row12M['AsOfDate']);
        if (date12M <= date3M) {
          value12M = n(row12M[metric]) || 0;
          break;
        }
      }

      // Subtract sum of last three used values (3M or 12M)
      const lastThreeSum = history[metric]
        ? history[metric].reduce((acc, val) => acc + val, 0)
        : 0;

      const adjustedValue = value12M - lastThreeSum;
      resolved[metric] = adjustedValue;

      // Update history with the adjusted value used
      if (!history[metric]) history[metric] = [];
      history[metric].push(adjustedValue);
      if (history[metric].length > 3) history[metric].shift();
    });

    //console.log(resolved)

    return resolved;
  });
}

export function generalAndAdmin(IS3M, IS12M) {
  if (n(IS3M['OperatingExpense']) - n(IS3M['ResearchAndDevelopment']) < 0) {
    if (n(IS3M['EBITDA']) - n(IS3M['TotalRevenue']) - (n(IS3M['CurrentAccruedExpenses']) + n(IS3M['NaturalResourceAssets'])) <= 0) {
      if (!IS12M) return 0;
      return n(IS12M['OperatingRevenue']);
    } else {
      return n(IS3M['EBITDA']) - n(IS3M['TotalRevenue']) - (n(IS3M['CurrentAccruedExpenses']) + n(IS3M['NaturalResourceAssets']));
    }
  }
  return n(IS3M['OperatingExpense']) - n(IS3M['ResearchAndDevelopment']);
}

export function accumulatedDepreciation(BS3M, IS3M, prevValue) {
  if (n(BS3M['AccumulatedDepreciation']) === 0) {
    return n(IS3M['ReconciledDepreciation']) + prevValue;
  } else {
    return n(BS3M['AccumulatedDepreciation']) * -1;
  }
}

function operatingIncome(IS3M, includeOtherIncome, IS12M, IS) {
    const generalAdmin = generalAndAdmin(IS3M, IS12M);
    return (
        (n(IS['TotalRevenue']) - n(IS['CostOfRevenue'])) 
        - n(generalAdmin) 
        - n(IS['ResearchAndDevelopment'])
        + (includeOtherIncome ? (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition'])) : 0)
    )
}

function EBITDA(IS3M, IS12M, includeOtherIncome, capRD, IS) {
    const generalAdmin = generalAndAdmin(IS3M, IS12M);
    return (
        (n(IS['TotalRevenue']) - n(IS['CostOfRevenue'])) 
        - n(generalAdmin) 
        - (capRD ? 0 : n(IS['ResearchAndDevelopment'])) 
        + n(IS['EarningsFromEquityInterest']) 
        + n(IS['ReconciledDepreciation'])
        + (includeOtherIncome ? (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition'])) : 0) 
    )
}

function cashFlowFromOps12M(cashFlowPrev, cashFlowFromOps) {
  let cashFlow12M = cashFlowFromOps;
  for (let i = 0; i < cashFlowPrev.length; i++) {
    cashFlow12M += n(cashFlowPrev[i]);
  }
  return cashFlow12M;
}

export function getTaxRate(IS, selectedTax, IS3M, IS12M, includeOtherIncome, capRD) {
  const ebitda = EBITDA(IS3M, IS12M, includeOtherIncome, capRD, IS);
  if (selectedTax) {
    return n(IS['TaxProvision']);
  }
  return ((n(ebitda - IS['ReconciledDepreciation']) - n(IS['InterestExpense'])) * 0.28);
}

export function getRatio(BS3M, numOfPeriods, sharesArray) {
  switch (numOfPeriods) {
    case 1:
      return n(BS3M['OrdinarySharesNumber']);
    case 2:
      return Average(sharesArray);
    case 3:
      return Average(sharesArray);
    case 4:
      return Average(sharesArray);
    case 5:
      return Average(sharesArray);
    default:
      return 0;
  }
}

export function getAssetLife(numOfPeriods, IS, assetLifeArray, depreciationArray) {
  switch (numOfPeriods) {
    case 1:
      if (n(IS['ReconciledDepreciation']) === 0) return 15;
      return assetLifeArray[0] / (n(IS['ReconciledDepreciation']) * 4);
    case 2:
      if (depreciationArray[0] + depreciationArray[1] === 0) return 15;
      return (Average(assetLifeArray)) / ((depreciationArray[0] + depreciationArray[1]) * 2);
    case 3:
      if (depreciationArray[0] + depreciationArray[1] + depreciationArray[2] === 0) return 15;
      return (Average(assetLifeArray)) / ((depreciationArray[depreciationArray.length - 3] + depreciationArray[depreciationArray.length - 2] + depreciationArray[depreciationArray.length - 1]) * (4/3));
    case 4:
      if (depreciationArray[0] + depreciationArray[1] + depreciationArray[2] + depreciationArray[3] === 0) return 15;
      return (Average(assetLifeArray)) / ((depreciationArray[depreciationArray.length - 4] + depreciationArray[depreciationArray.length - 3] + depreciationArray[depreciationArray.length - 2] + depreciationArray[depreciationArray.length - 1]));
    default:
      if (depreciationArray[0] + depreciationArray[1] + depreciationArray[2] + depreciationArray[3] === 0) return 15;
      return (Average(assetLifeArray)) / ((depreciationArray[depreciationArray.length - 4] + depreciationArray[depreciationArray.length - 3] + depreciationArray[depreciationArray.length - 2] + depreciationArray[depreciationArray.length - 1]));  }
}

export function findClosestDate(target, rates) {
  const targetDate = new Date(target);

  // get all the date keys (ignore "Date")
  const keys = Object.keys(rates).filter(k => k !== "Date");

  // sort ascending
  keys.sort((a, b) => new Date(a) - new Date(b));

  if (keys.length === 0) return null;

  let closest = null;
  const firstKey = keys[0];

  for (const d of keys) {
    const current = new Date(d);

    if (current.getTime() === targetDate.getTime()) {
      // exact match
      return d;
    }

    if (current > targetDate) {
      // we've passed the target, so use the previous one
      return closest ?? firstKey; // if no previous, use first key
    }

    // update closest (still <= targetDate)
    closest = d;
  }

  // if target is greater than all keys, return the last one
  return closest;
}

export function fillLeadingZerosWithFirstValue(obj) {
  // get sorted date keys
  const keys = Object.keys(obj)
    .filter(k => /^\d{4}-\d{2}-\d{2}$/.test(k))
    .sort((a, b) => new Date(a) - new Date(b));

  // find first non-zero (and non-empty string) value
  const firstValue = keys.map(k => obj[k])
    .find(v => v !== 0 && v !== "0" && v !== "");

  if (firstValue === undefined) return obj; // nothing to fill

  // create new object, replacing leading zeros
  const filled = {};
  let hasFoundFirst = false;

  for (const k of keys) {
    const val = obj[k];
    if (!hasFoundFirst && (val === 0 || val === "0" || val === "")) {
      filled[k] = firstValue;
    } else {
      filled[k] = val;
      if (val !== 0 && val !== "0" && val !== "") {
        hasFoundFirst = true;
      }
    }
  }

  return filled;
}

export function growthRate(current, previous) {
  if (previous === null || isNaN(previous) || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

export function CFIRR(nper, pmt, pv, fv = 0, type = 0, guess = 0.1) {
  const epsMax = 1e-10;
  const iterMax = 100;
  let rate = guess;

  for (let i = 0; i < iterMax; i++) {
    const pow = Math.pow(1 + rate, nper);
    const y = pv * pow + pmt * (1 + rate * type) * (pow - 1) / rate + fv;
    const yDeriv = pv * nper * Math.pow(1 + rate, nper - 1) +
      pmt * (1 + rate * type) * ((pow - 1) / (rate * rate) - nper * pow / rate);

    const newRate = rate - y / yDeriv;
    if (Math.abs(newRate - rate) < epsMax) return newRate;
    rate = newRate;
  }

  return rate;
}


// ---------------- Financial Summary Calculation ----------------
export async function calculateFinancialSummary(newcapRD, newincludeOtherIncome, newselectedTax) {

  const capRD = newcapRD;
  const includeOtherIncome = newincludeOtherIncome;
  const selectedTax = newselectedTax;

  const incomeStatement3M = await getTableData("Income Statement 3M");
  const balanceSheet3M = await getTableData("Balance Sheet 3M");
  const earningReports3M = await getTableData("Earnings Reports 3M");
  const incomeStatement12M = await getTableData("Income Statement 12M");
  const balanceSheet12M = await getTableData("Balance Sheet 12M");
  const earningReports12M = await getTableData("Earnings Reports 12M");
  const ratesTable = await getTableData("Rates");
  const PVLongTerm = await getTableData("PVLongTerm");
  const QuarterlyPrices = await getTableData("QuarterlyPrice");
  const rates = ratesTable[0];
  const prices = QuarterlyPrices[1];
  const exchangeRate = QuarterlyPrices[3];

  if (!incomeStatement3M || !balanceSheet3M || !earningReports3M || !incomeStatement12M) return [];

  const rows = [];
  //helper variables
  let aDepPrev = 0;
  let naCAPrev = 0;
  let CFPrev = [0, 0, 0];
  let CF2Prev = [0, 0, 0, 0];
  let numOfPeriods = 0;
  let sharesArray = [];
  let assetLifeArray = [];
  let depreciationArray = [];
  let annualAssetLifeArray = [];
  let annualCapitalExcl = [];
  let assetLifeArrayInclIntangible = [];
  let annualAssetLifeArrayInclIntangible = [];
  let annualCapitalIncl = [];
  let netFixedCapitalArray = [];
  let InflAdjGrossFixedCapitalArray = [];
  let InflAdjNetFixedCapitalArray = [];
  let grossInvestedCapitalExclArray = [];
  let grossInvestedCapitalExclArray2 = [];
  let grossInvestedCapitalInclArray = [];
  let netInvestedCapitalArray = [];
  let inflationTableValue = 0;
  let salvageValueArray = [];
  let salvageValueArray2 = [];
  let cashFlowArray = [];
  let interestExpenseArray = [];
  let plowbackNumerator = [];
  let plowbackDenominator = [];
  //CFIRR Variables
  let cfirrTFQIncl = 0;
  let cfirrTFQExcl = 0;
  let cfirrAnnualQIncl = 0;
  let cfirrAnnualQExcl = 0;
  let cashFlowPerShareArray = [];
  let cashFlowPerShareArray2 = [];
  let tfqDividendsArray = [];
  let tfqDividends = 0;
  let cashAvailable = 0;
  let payoutRatio = 0;
  let dividendGrowth = 0;
  let totalRevenueArray = [];
  let totalRevenueGrw = 0;
  let ebitdaArray = [];
  let ebitdaGrw = 0;
  let ebitdaSum = 0;
  //----------------------------------
  const adjustedIncomeStatements = getRowValue(incomeStatement3M, incomeStatement12M);
  
  for (let i = 0; i < incomeStatement3M.length; i++) {
    const IS3M = incomeStatement3M[i], BS3M = balanceSheet3M[i], ER3M = earningReports3M[i], IS12M = incomeStatement12M[i];
    const IS = adjustedIncomeStatements[i];
    
    if (!IS3M || IS3M['AsOfDate'] === 0) continue;

      naCAPrev = n(IS['ResearchAndDevelopment']) + naCAPrev;

        const derived = {
          costOfSales: n(IS['CostOfRevenue']) + n(IS['ReconciledDepreciation']),
          grossMarginIncl: n(IS['TotalRevenue']) - (n(IS['CostOfRevenue']) + n(IS['ReconciledDepreciation'])),
          grossMarginExcl: n(IS['TotalRevenue']) - n(IS['CostOfRevenue']),
          generalAndAdmin: n(generalAndAdmin(IS, IS12M)), 
          otherIncome: n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition']),
          operatingIncome: operatingIncome(IS3M, includeOtherIncome, IS12M, IS),
          ebitda: EBITDA(IS3M, IS12M, includeOtherIncome, capRD, IS),
          otherCurrentAssets: n(BS3M['CurrentAssets']) - ((Math.max(n(BS3M['AccountsReceivable']), n(BS3M['Receivables']))) + n(BS3M['Inventory']) + n(BS3M['PrepaidAssets']) + n(BS3M['CurrentDeferredTaxesAssets']) + n(BS3M['CashAndCashEquivalents']) + n(BS3M['RestrictedCash']) + n(BS3M['OtherShortTermInvestments'])),
          totalCurrentAssets: (n(BS3M['CurrentAssets']) - ((Math.max(n(BS3M['AccountsReceivable']), n(BS3M['Receivables']))) + n(BS3M['Inventory']) + n(BS3M['PrepaidAssets']) + n(BS3M['CurrentDeferredTaxesAssets']) + n(BS3M['CashAndCashEquivalents']) + n(BS3M['RestrictedCash']) + n(BS3M['OtherShortTermInvestments']))) + n(BS3M['CashAndCashEquivalents']) + n(BS3M['RestrictedCash']) + n(BS3M['OtherShortTermInvestments']) + (Math.max(n(BS3M['AccountsReceivable']), n(BS3M['Receivables']))) + n(BS3M['Inventory']) + n(BS3M['PrepaidAssets']) + n(BS3M['CurrentDeferredTaxesAssets']),
          accumulatedDepreciation: n(accumulatedDepreciation(BS3M, IS3M, aDepPrev)),
          otherIntangibleAssets: n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill']),
          otherNonCurrentAssets: n(BS3M['TotalNonCurrentAssets']) - (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))),
          investmentsAndAdvances: n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']),
          totalNonCurrentAssets: n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['TotalNonCurrentAssets']) - (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets'])))) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets'])),
          otherMiscLiabilities: n(BS3M['CurrentLiabilities']) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable']) + n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities'])),
          totalCurrentLiabilities: n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable']) + n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']) + (n(BS3M['CurrentLiabilities']) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable']) + n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']))),
          nonCurrentMiscLiabilities: n(BS3M['TotalNonCurrentLiabilitiesNetMinorityInterest']) - (n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) + n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities'])),
          totalNonCurrentLiabilities: n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) + n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + (n(BS3M['TotalNonCurrentLiabilitiesNetMinorityInterest']) - (n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) + n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']))),
          commonStock: n(BS3M['CommonStockEquity']) - n(BS3M['PreferredStockOfSubsidiary']) - n(BS3M['PreferredSecuritiesOutsideStockEquity']),
          totalOwnerEquity: n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) + (n(BS3M['CommonStockEquity']) - n(BS3M['PreferredStockOfSubsidiary']) - n(BS3M['PreferredSecuritiesOutsideStockEquity'])),
        //CFandIncome
          nonOperationIncome: n(IS['InterestIncome']) + n(IS['RestructuringAndMergernAcquisition']) + n(IS['AcquiredInProcessRnDIncome']) + n(IS['SpecialIncomeCharges']) + (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition'])),
          nonOperationIncomeAT: 0.72 * (n(IS['InterestIncome']) + n(IS['RestructuringAndMergernAcquisition']) + n(IS['AcquiredInProcessRnDIncome']) + n(IS['SpecialIncomeCharges'])) + (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition'])),
          totalOtherIncome: n(IS['NetIncomeDiscontinuousOperations']) + n(IS['NetIncomeExtraordinary']) + n(IS['CumulativeEffectOfAccountingChange']) + n(IS['NetIncomeFromTaxLossCarryforward']) + n(IS['OtherOperatingExpenses']),
          userTax: getTaxRate(IS, selectedTax, IS3M, IS12M, includeOtherIncome, capRD),
        //Invested Capital
          totalOtherFixedCapital:  n(BS3M['Goodwill']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(BS3M['NonCurrentDeferredAssets']) + n(BS3M['TotalNonCurrentAssets']) - (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets'])),
          investedCapitalFinancing: n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) + n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + (n(BS3M['CurrentLiabilities']) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable']) + n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']))) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) + (n(BS3M['CommonStockEquity']) - n(BS3M['PreferredStockOfSubsidiary']) - n(BS3M['PreferredSecuritiesOutsideStockEquity'])),
          debtAndEquivalents: n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + (n(BS3M['CurrentLiabilities']) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable']) + n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']))) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']),
        }

        // Get net working liabilities
        const getCurrentLiabilities = (BS3M, derived) =>
          n(BS3M['AccountsPayable']) +
          n(BS3M['DividendsPayable']) +
          n(BS3M['CurrentNotesPayable']) +
          n(BS3M['CurrentAccruedExpenses']) +
          n(BS3M['CurrentDeferredLiabilities']) +
          n(BS3M['TotalTaxPayable']) +
          n(BS3M['OtherCurrentLiabilities']) +
          n(derived.otherMiscLiabilities);

        // --- Period counter ---
        if (numOfPeriods < 5) numOfPeriods++;

        // --- Net Income ---
        const specialItems =
          n(IS['RestructuringAndMergernAcquisition']) +
          n(IS['AcquiredInProcessRnDIncome']) +
          n(IS['SpecialIncomeCharges']);

        const otherIncomeAdj =
          n(IS3M['OtherIncomeExpense']) -
          n(IS3M['SpecialIncomeCharges']) -
          n(IS3M['RestructuringAndMergernAcquisition']);

        const netIncome =
          (
            n(derived.ebitda) -
            n(IS['ReconciledDepreciation']) +
            n(IS['InterestIncome']) +
            specialItems +
            otherIncomeAdj -
            n(IS['InterestExpense']) -
            n(IS['EarningsFromEquityInterest']) -
            n(IS['TaxProvision'])
          ) +
          n(IS['NetIncomeDiscontinuousOperations']) +
          n(IS['NetIncomeExtraordinary']) +
          n(IS['CumulativeEffectOfAccountingChange']) +
          n(IS['NetIncomeFromTaxLossCarryforward']) +
          n(IS['OtherOperatingExpenses']) +
          n(IS['MinorityInterests']);

        const netPPE = n(BS3M['NetPPE']);
        const grossPPE = n(BS3M['GrossPPE']);
        const accumulatedDep = n(derived.accumulatedDepreciation);

        const grossFixedAssets =
          Math.max((netPPE + accumulatedDep), grossPPE) +
          ((capRD ? naCAPrev : 0))

        // --- Cash Flow from Ops ---
        const cashFlowFromOps =
          n(netIncome) -
          n(derived.nonOperationIncome) -
          n(derived.totalOtherIncome) +
          0.72 * n(IS['InterestExpense']) +
          n(IS['MinorityInterests']) +
          n(IS['ReconciledDepreciation']) -
          (n(derived.userTax) - n(IS['TaxProvision']));

        const cashFlowFromOps2 =
          n(derived.ebitda) - n(derived.userTax) - 0.28 * n(IS['InterestExpense']);

        aDepPrev = derived.accumulatedDepreciation;

        // --- Shares ---
        sharesArray.push(n(BS3M['OrdinarySharesNumber']));
        const sharesRatio = getRatio(BS3M, numOfPeriods, sharesArray);

        if (sharesArray.length === 5) sharesArray.shift();

        // --- Asset Life ---

        assetLifeArray.push(
            grossFixedAssets +
            n(derived.otherNonCurrentAssets) +
            n(derived.investmentsAndAdvances) +
            n(BS3M['Inventory'])
        );


        depreciationArray.push(n(IS['ReconciledDepreciation']));

        assetLifeArrayInclIntangible.push(
          grossFixedAssets +
            n(IS['ResearchAndDevelopment']) +
            n(BS3M['Goodwill']) +
            n(derived.otherIntangibleAssets) +
            n(derived.otherNonCurrentAssets) +
            n(derived.investmentsAndAdvances) +
            n(BS3M['Inventory'])
        );

        const assetLife = (Math.min(n(getAssetLife(numOfPeriods, IS, assetLifeArray, depreciationArray)), 30));
        const assetLifeInclIntangibles = (Math.min(n(getAssetLife(numOfPeriods, IS, assetLifeArrayInclIntangible, depreciationArray)), 30));

        annualAssetLifeArray.push(assetLife);
        annualAssetLifeArrayInclIntangible.push(assetLifeInclIntangibles);

        const averageAssetLife = (Math.max(3, Average(annualAssetLifeArray)));
        const averageAssetLifeInclIntangible = (Math.max(3, Average(annualAssetLifeArrayInclIntangible)));

        if (annualAssetLifeArray.length === 12) annualAssetLifeArray.shift();
        if (annualAssetLifeArrayInclIntangible.length === 12) annualAssetLifeArrayInclIntangible.shift();

        // --- Inflation ---

        const yearIndex =
          Math.ceil(
            Math.min(30, averageAssetLife) *
              (1 - netPPE / (netPPE + accumulatedDep))
          ) * 365;

        const inflationAge = new Date(IS3M['AsOfDate']).getTime() - yearIndex * 86400 * 1000;
        const inflationDate = findClosestDate(new Date(IS3M['AsOfDate']).getTime(), rates);
        const inflationAgeDate = findClosestDate(new Date(inflationAge).getTime(), rates);

        const inflationTableAge = rates[inflationAgeDate];

        if (rates[IS3M['AsOfDate']] !== undefined) {
          inflationTableValue = n(rates[IS3M['AsOfDate']]);
        } else {
          inflationTableValue = rates[inflationDate];
        }

        // --- Price ---
        const rawPriceDate = new Date(IS3M['AsOfDate']);
        rawPriceDate.setDate(rawPriceDate.getDate() + 45);
        const priceDate = findClosestDate(rawPriceDate, prices);

        // --- Growth Rate Values ---
        const currentLiabilities = getCurrentLiabilities(BS3M, derived);

        netFixedCapitalArray.push(
          netPPE + n(BS3M['NonCurrentDeferredAssets']) + n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances)
        );

        InflAdjGrossFixedCapitalArray.push(
          (n(derived.totalOtherFixedCapital) - 
            n(BS3M['Goodwill']) - (n(BS3M['GoodwillAndOtherIntangibleAssets']) - 
            n(BS3M['Goodwill'])) + n(grossFixedAssets) *
            (n(inflationTableValue) / n(rates[inflationDate]))
        ));

        InflAdjNetFixedCapitalArray.push(
          (netPPE +
            n(BS3M['NonCurrentDeferredAssets']) +
            n(derived.otherNonCurrentAssets) +
            n(derived.investmentsAndAdvances)) *
            (n(inflationTableValue) / n(rates[inflationDate]))
        );

        grossInvestedCapitalExclArray.push(
          (n(derived.totalOtherFixedCapital) - 
            n(BS3M['Goodwill']) - (n(BS3M['GoodwillAndOtherIntangibleAssets']) - 
            n(BS3M['Goodwill'])) + n(grossFixedAssets)) *
            (n(inflationTableValue) / n(rates[inflationDate])) +
            Math.max(n(derived.totalCurrentAssets) - currentLiabilities, 0)
        );

        netInvestedCapitalArray.push(
          (netPPE +
            n(BS3M['NonCurrentDeferredAssets']) +
            n(derived.otherNonCurrentAssets) +
            n(derived.investmentsAndAdvances)) *
            (n(inflationTableValue) / n(rates[inflationDate])) +
            (n(derived.totalCurrentAssets) - currentLiabilities)
        );

        // --- Total Revenue Growth ---
        totalRevenueArray.push(n(IS['TotalRevenue']));
        if (totalRevenueArray.length > 4) {
          totalRevenueGrw = ((totalRevenueArray.at(-1) / totalRevenueArray.at(-5)) - 1) * 100;
          totalRevenueArray.shift();
        }

        // --- EBITDA Growth ---
        if (ebitdaArray.length > 3) {
          ebitdaSum = ebitdaArray.reduce((a, b) => a + b, 0);
        }

        ebitdaArray.push(n(derived.ebitda));
        const ebitdaMargin = n(derived.ebitda) / n(IS['TotalRevenue']) * 100;
        if (ebitdaArray.length > 4) {
          ebitdaGrw = ((ebitdaArray.at(-1) / ebitdaArray.at(-5)) - 1) * 100;
          ebitdaArray.shift();
        }

        // Growth Rates
        const netFixedCapitalGrw = growthRate(netFixedCapitalArray.at(-1), netFixedCapitalArray.at(-5));
        const inflAdjGrossFixedCapitalGrw = growthRate(InflAdjGrossFixedCapitalArray.at(-1), InflAdjGrossFixedCapitalArray.at(-5));
        const inflAdjNetFixedCapitalGrw = growthRate(InflAdjNetFixedCapitalArray.at(-1), InflAdjNetFixedCapitalArray.at(-5));
        const grossInvestedCapitalGrw = growthRate(grossInvestedCapitalExclArray.at(-1), grossInvestedCapitalExclArray.at(-5));
        const netInvestedCapitalGrw = growthRate(netInvestedCapitalArray.at(-1), netInvestedCapitalArray.at(-5));


        // --- CFIRR ---
        cashFlowArray.push(cashFlowFromOps);

        const salvageValue = Math.max(
          0,
          n(derived.totalCurrentAssets) -
            (n(derived.totalCurrentLiabilities) - n(BS3M['CurrentDebtAndCapitalLeaseObligation']))
        );

        salvageValueArray.push(salvageValue);
        salvageValueArray2.push(salvageValue);

        const cashFlowSum = cashFlowArray.reduce((a, b) => a + b, 0);
        const salvageValueAvg = Average(salvageValueArray);


        const grossInvestedCapitalIncl =
          (n(derived.totalOtherFixedCapital) +
            grossFixedAssets) *
            (n(inflationTableValue) / n(inflationTableAge)) +
          Math.max(n(derived.totalCurrentAssets) - currentLiabilities, 0);

        const grossInvestedCapitalExcl =
          (n(derived.totalOtherFixedCapital) -
            n(BS3M['Goodwill']) -
            (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) +
            grossFixedAssets) *
            (n(inflationTableValue) / n(inflationTableAge)) +
          Math.max(n(derived.totalCurrentAssets) - currentLiabilities, 0);

        grossInvestedCapitalInclArray.push(grossInvestedCapitalIncl);
        grossInvestedCapitalExclArray2.push(grossInvestedCapitalExcl);
        annualCapitalIncl.push(grossInvestedCapitalIncl);
        annualCapitalExcl.push(grossInvestedCapitalExcl);

        // Quarterly CFIRR
        if (cashFlowArray.length > 1) {
          const avgSalvageValue = Average(salvageValueArray2);
          const pvIncl = Average(annualCapitalIncl) * -1;
          const pvExcl = Average(annualCapitalExcl) * -1;

          cfirrAnnualQExcl = CFIRR(averageAssetLife, cashFlowFromOps * 4, pvExcl, avgSalvageValue, 0, 0.07);
          cfirrAnnualQIncl = CFIRR(averageAssetLifeInclIntangible, cashFlowFromOps * 4, pvIncl, avgSalvageValue, 0, 0.07);

          annualCapitalExcl.shift();
          annualCapitalIncl.shift();
          salvageValueArray2.shift();
        }

        // Rolling 4-period CFIRR

        // TFQ CFIRR
        if (grossInvestedCapitalInclArray.length === 5) {
          const pvValueIncl = Average(grossInvestedCapitalInclArray) * -1;
          const pvValueExcl = Average(grossInvestedCapitalExclArray2) * -1;

          cfirrTFQExcl = CFIRR(assetLife, cashFlowSum, pvValueExcl, salvageValueAvg, 0, 0.07);
          cfirrTFQIncl = CFIRR(assetLifeInclIntangibles, cashFlowSum, pvValueIncl, salvageValueAvg, 0, 0.07);

          salvageValueArray.shift();
          grossInvestedCapitalInclArray.shift();
          grossInvestedCapitalExclArray2.shift();
          // grossInvestedCapitalExclArray2.shift();
        }

        if (cashFlowArray.length === 4) {
          cashFlowArray.shift();
          assetLifeArray.shift();
          assetLifeArrayInclIntangible.shift();
        }

        // --- Plowback ---
        plowbackNumerator.push(cashFlowFromOps);
        plowbackDenominator.push(grossInvestedCapitalExcl);
        interestExpenseArray.push(0.72 * n(IS['InterestExpense']));

        // Rolling sums for plowback
        const cashflowPlowback = [5, 6, 7, 8].map((i) => n(plowbackNumerator.at(-i))).reduce((a, b) => a + b, 0);
        const interestExpensePlowback = [5, 6, 7, 8].map((i) => n(interestExpenseArray.at(-i))).reduce((a, b) => a + b, 0);

        const plowbackRatio =
          ((cashflowPlowback - interestExpensePlowback) / plowbackDenominator.at(-5)) * 100;

        // --- Dividends ----

        const cashFlowPerShare = (cashFlowFromOps - (0.72 * n(IS['InterestExpense']))) / n(ER3M['BasicAverageShares'] / 1000000);
        cashFlowPerShareArray.push(cashFlowPerShare);
        cashFlowPerShareArray2.push(cashFlowPerShare);

        tfqDividendsArray.push(n(ER3M['DividendPerShare']));

        if (cashFlowPerShareArray.length === 4) {
          cashAvailable = cashFlowPerShareArray.reduce((a, b) => a + b, 0);
          tfqDividends = tfqDividendsArray.reduce((a, b) => a + b, 0);
          cashFlowPerShareArray.shift();
          tfqDividendsArray.shift();
          if (cashAvailable === 0) {
            payoutRatio = 0;
          } else {
            payoutRatio = (tfqDividends / cashAvailable) * 100;
          }
        }

        if (cashFlowPerShareArray2.length > 7) {
          const cashflowPrevValue = [5, 6, 7, 8].map((i) => n(cashFlowPerShareArray2.at(-i))).reduce((a, b) => a + b, 0);
          dividendGrowth = growthRate(cashflowPrevValue, tfqDividends);
        } 

        // Dividends Yield
        const exchangeDate = findClosestDate(rawPriceDate, exchangeRate);
        const dividendYield = ((tfqDividends * n(exchangeRate[exchangeDate])) / n(prices[priceDate])) * 100;
        
        //Quartetly Rows
        rows.push({ 
          // Income Statement Values
          Time: "Quarterly",
          Date: IS3M['AsOfDate'], 
          operatingRevenue: n(IS['OperatingRevenue']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          otherTaxes: n(IS['OtherTaxes']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalRevenue: n(IS['TotalRevenue']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalRevenueGrw: (n(totalRevenueGrw)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          costOfRevenue: n(IS['CostOfRevenue']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          costOfSales: n(derived.costOfSales).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          grossMarginIncl: n(derived.grossMarginIncl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          grossMarginExcl: n(derived.grossMarginExcl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          generalAndAdmin: n(derived.generalAndAdmin).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          researchAndDevelopment: n(IS['ResearchAndDevelopment']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          otherIncome: n(derived.otherIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          operatingIncome: n(derived.operatingIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          equityInterest: n(IS['EarningsFromEquityInterest']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          ebitda: n(derived.ebitda).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          ebitdaMargin: (n(ebitdaMargin)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          ebitdaGrw: (n(ebitdaGrw)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          depreciation: n(IS['ReconciledDepreciation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          ebit: (n(derived.ebitda - IS['ReconciledDepreciation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })),
          interestIncome: n(IS['InterestIncome']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeRest: n(IS['RestructuringAndMergernAcquisition']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeAcquired: n(IS['AcquiredInProcessRnDIncome']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          specialIncome: n(IS['SpecialIncomeCharges']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeAvail: (n(derived.ebitda) - n(IS['ReconciledDepreciation']) + n(IS['InterestIncome']) + n(IS['RestructuringAndMergernAcquisition']) + n(IS['AcquiredInProcessRnDIncome']) + n(IS['SpecialIncomeCharges']) + (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          interestExpense: n(IS['InterestExpense']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          beforeTaxIncome: ((n(derived.ebitda) - n(IS['ReconciledDepreciation']) + n(IS['InterestIncome']) + n(IS['RestructuringAndMergernAcquisition']) + n(IS['AcquiredInProcessRnDIncome']) + n(IS['SpecialIncomeCharges']) + (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition']))) - n(IS['InterestExpense']) - n(IS['EarningsFromEquityInterest'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeTax: n(IS['TaxProvision']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromCO: (((n(derived.ebitda) - n(IS['ReconciledDepreciation']) + n(IS['InterestIncome']) + n(IS['RestructuringAndMergernAcquisition']) + n(IS['AcquiredInProcessRnDIncome']) + n(IS['SpecialIncomeCharges']) + (n(IS3M['OtherIncomeExpense']) - n(IS3M['SpecialIncomeCharges']) - n(IS3M['RestructuringAndMergernAcquisition']))) - n(IS['InterestExpense']) - n(IS['EarningsFromEquityInterest'])) - IS['TaxProvision']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromDO: n(IS['NetIncomeDiscontinuousOperations']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromEI: n(IS['NetIncomeExtraordinary']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromAC: n(IS['CumulativeEffectOfAccountingChange']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromTLC: n(IS['NetIncomeFromTaxLossCarryforward']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromOG: n(IS['OtherOperatingExpenses']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          minorityInterests: n(IS['MinorityInterests']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncome: netIncome.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          preferredDividends: n(IS['PreferredStockDividends']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeAvailable: (netIncome - n(IS['PreferredStockDividends'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          sharesOutstanding: n(BS3M['OrdinarySharesNumber']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsContinuingOps: n(ER3M['BasicContinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsDiscontinuedOps: n(ER3M['BasicDiscontinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsExtraordinaryINC: n(ER3M['BasicExtraordinary']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsAccountingChg: n(ER3M['BasicAccountingChange']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsOtherGains: n(ER3M['BasicEPSOtherGainsLosses']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsTotal: n(ER3M['BasicEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          normalizedBasicEps: n(ER3M['NormalizedBasicEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicAverageShares: n(ER3M['BasicAverageShares'] / 1000000).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
          dilutedEpsContinuingOps: n(ER3M['DilutedContinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsDiscontinuedOps: n(ER3M['DilutedDiscontinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsExtraordinaryINC: n(ER3M['DilutedExtraordinary']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsAccountingChg: n(ER3M['DilutedAccountingChange']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsOtherGains: n(ER3M['DilutedEPSOtherGainsLosses']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsTotal: n(ER3M['DilutedEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          normalizedDilutedEps: n(ER3M['NormalizedDilutedEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedAverageShares: n(ER3M['DilutedAverageShares'] / 1000000).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
          dividendPerShare: n(ER3M['DividendPerShare']).toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
          // Balance Sheet Values
          // Assets
          cashAndEquivalents: n(BS3M['CashAndCashEquivalents']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          restrictedCash: n(BS3M['RestrictedCash']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          marketableSecurities: n(BS3M['OtherShortTermInvestments']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          accountsReceivable: Math.max(n(BS3M['AccountsReceivable']), n(BS3M['Receivables'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          inventory: n(BS3M['Inventory']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          prepaidExpenses: n(BS3M['PrepaidAssets']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          currentDeferredAssets: n(BS3M['CurrentDeferredTaxesAssets']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherCurrentAssets: n(derived.otherCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalCurrentAssets: n(derived.totalCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          capitalizedRD: (naCAPrev).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          accumulatedDepreciation: n(derived.accumulatedDepreciation).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          grossFixedAssets: n(grossFixedAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          netPremises: n(BS3M['NetPPE']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          goodwill: n(BS3M['Goodwill']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherIntangibleAssets: n(derived.otherIntangibleAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentDeferredAssets: n(BS3M['NonCurrentDeferredAssets']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherNonCurrentAssets: n(derived.otherNonCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          investmentsAndAdvances: n(derived.investmentsAndAdvances).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalNonCurrentAssets: n(derived.totalNonCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalAssets: n(derived.totalCurrentAssets + derived.totalNonCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          // Liabilities
          accountsPayable: n(BS3M['AccountsPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dividendsPayable: n(BS3M['DividendsPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          notesPayable: n(BS3M['CurrentNotesPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          shortTermDebt: n(BS3M['CurrentDebtAndCapitalLeaseObligation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          accruedExpenses: n(BS3M['CurrentAccruedExpenses']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          deferredRevenue: n(BS3M['CurrentDeferredLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          currentDeferredIncomeTaxes: n(BS3M['TotalTaxPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherCurrentLiabilities: n(BS3M['OtherCurrentLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherMiscLiabilities: n(derived.otherMiscLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalCurrentLiabilities: n(derived.totalCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          longTermDebt: n(BS3M['LongTermDebt']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          capitalLeaseObligations: n(BS3M['LongTermCapitalLeaseObligation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          deferredIncomeTaxes: n(BS3M['NonCurrentDeferredLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherDeferredTaxAssets: n(BS3M['NonCurrentAccruedExpenses']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          derivativeLiabilities: n(BS3M['DerivativeProductLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          retirementBenefits: n(BS3M['EmployeeBenefits']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          longTermProvisions: n(BS3M['LongTermProvisions']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          regulatoryLiabilities: n(BS3M['RegulatoryLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentOtherLiabilities: n(BS3M['OtherNonCurrentLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentMiscLiabilities: n(derived.nonCurrentMiscLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalNonCurrentLiabilities: n(derived.totalNonCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalLiabilities: n(derived.totalCurrentLiabilities + derived.totalNonCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          // Equity
          minorityInterestsBal: n(BS3M['MinorityInterest']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          preferredSecurities: n(BS3M['PreferredStockOfSubsidiary']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          outsideStockEquity: n(BS3M['PreferredSecuritiesOutsideStockEquity']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          stockEquity: n(BS3M['PreferredStockEquity']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          commonStock: n(derived.commonStock).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOwnerEquity: n(derived.totalOwnerEquity).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalLiabilitiesAndEquity: n(derived.totalCurrentLiabilities + derived.totalNonCurrentLiabilities + derived.totalOwnerEquity).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          additionalPaidInCapital: n(BS3M['AdditionalPaidInCapital']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          retainedEarnings: n(BS3M['RetainedEarnings']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          treasuryStock: (n(BS3M['TreasuryStock']) * -1).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherComprehensiveIncome: n(BS3M['GainsLossesNotAffectingRetainedEarnings']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),

          //CF and Income
          taxRate: "28%",
          nonOperationIncome: n(derived.nonOperationIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonOperationIncomeAT: n(derived.nonOperationIncomeAT).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherIncome: n(derived.totalOtherIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          interestExpenseAT: (0.72 * n(IS['InterestExpense'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          calculatedTax: ((n(derived.ebitda - IS['ReconciledDepreciation']) - n(IS['InterestExpense'])) * 0.28).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          chosenTax: n(derived.userTax).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cashFlowFromOps: (cashFlowFromOps).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          taxShield: (0.28 * n(IS['InterestExpense'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cashFlowFromOps12M: (cashFlowFromOps12M(CFPrev, cashFlowFromOps)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cashFlowFromOpsAnnualized: (cashFlowFromOps * 4).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          secondCashFlow: (derived.ebitda - derived.userTax - (0.28 * n(IS['InterestExpense']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          secondCashFlow12M: (cashFlowFromOps12M(CF2Prev, cashFlowFromOps2)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          secondCashFlowAnnualized: (cashFlowFromOps2 * 4).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          numOfPeriods: numOfPeriods,
          ebitdaPerShare: sharesRatio === 0 ? "N/A" : "$" + (derived.ebitda / n(sharesRatio) || 0).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          cfFromOpsPerShare: sharesRatio === 0 ? "N/A" : "$" + ((derived.ebitda - derived.userTax - (0.28 * n(IS['InterestExpense']))) / n(sharesRatio)).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          cfInterest: (cashFlowFromOps - (0.72 * n(IS['InterestExpense']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cfInterestPerShare: sharesRatio === 0 ? "N/A" : "$" + ((cashFlowFromOps - (0.72 * n(IS['InterestExpense']))) / n(sharesRatio)).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),

        //Invested Capital
          // Working capital
          netWorkingCapital: n(derived.totalCurrentAssets - derived.totalCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netWorkingCapitalExclDebt: (n(derived.totalCurrentAssets) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable'])
          + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']) + n(derived.otherMiscLiabilities))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCashWorkingCapital: n(n(Math.max(n(BS3M['AccountsReceivable']), n(BS3M['Receivables']))) + n(BS3M['Inventory']) + n(BS3M['PrepaidAssets']) + n(BS3M['CurrentDeferredTaxesAssets']) + n(derived.otherCurrentAssets)
          - n(BS3M['AccountsPayable']) - n(BS3M['DividendsPayable']) - n(BS3M['CurrentNotesPayable']) - n(BS3M['CurrentAccruedExpenses']) - n(BS3M['CurrentDeferredLiabilities']) - n(BS3M['TotalTaxPayable']) - n(BS3M['OtherCurrentLiabilities']) - n(derived.otherMiscLiabilities)),
          //Fixed Capital
          //fixedAssets: (n(BS3M['NetPPE']) + n(derived.accumulatedDepreciation)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherFixedCapital: n(derived.totalOtherFixedCapital).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherFixedCapitalIncl: (n(derived.totalOtherFixedCapital) + n(grossFixedAssets)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherFixedCapitalExcl: (n(derived.totalOtherFixedCapital) - n(BS3M['Goodwill']) - (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(grossFixedAssets)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netFixedCapitalInclIntangible: (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(BS3M['NonCurrentDeferredAssets']) + n(BS3M['TotalNonCurrentAssets']) - (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netFixedCapitalExclIntangible: (n(BS3M['NetPPE']) + n(BS3M['NonCurrentDeferredAssets']) + n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netFixedCapitalExclIntangibleGrw: netFixedCapitalGrw === null ? "0" : (netFixedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          investedCapitalAsset: (n(derived.totalCurrentAssets) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable'])
          + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']) + n(derived.otherMiscLiabilities)) + (n(derived.totalOtherFixedCapital) + n(BS3M['NetPPE']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          investedCapitalFinancing: (n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) +  n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + n(derived.nonCurrentMiscLiabilities) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) + n(derived.commonStock)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          investedCapitalDifference: (n(derived.totalCurrentAssets) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable'])
          + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']) + n(derived.otherMiscLiabilities)) + (n(derived.totalOtherFixedCapital) + n(BS3M['NetPPE'])) - (n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) +  n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + n(derived.nonCurrentMiscLiabilities) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) + n(derived.commonStock))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          debtAndEquivalents: (n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) +  n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + n(derived.nonCurrentMiscLiabilities) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) - n(BS3M['NonCurrentDeferredLiabilities']) - n(BS3M['NonCurrentAccruedExpenses'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          //Inflation Adjustment
          netPlantOverGrossPlant: ((n(BS3M['NetPPE']) / (n(BS3M['NetPPE']) + n(derived.accumulatedDepreciation))) * 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          averageAge: ((n(Math.min(30, averageAssetLife)).toFixed(0)) * (1 - ((n(BS3M['NetPPE']) / (n(BS3M['NetPPE']) + n(derived.accumulatedDepreciation)))))),
          inflationTablePeriod: n(inflationTableValue).toFixed(3),
          inflationTableAge: n(inflationTableAge).toFixed(3),
          inflationFactor: ((n(inflationTableValue)) / n(inflationTableAge)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          InflAdjGrossFixedCapitalIncl: ((n(derived.totalOtherFixedCapital) + n(grossFixedAssets)) * ((n(inflationTableValue)) / n(inflationTableAge))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjGrossFixedCapitalExcl: ((n(derived.totalOtherFixedCapital) - n(BS3M['Goodwill']) - (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(grossFixedAssets)) * ((n(inflationTableValue)) / n(inflationTableAge))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjGrossFixedCapitalExclGrw: inflAdjGrossFixedCapitalGrw === null ? "0" : ((inflAdjGrossFixedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"),
          InflAdjNetFixedCapitalIncl: ((n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(BS3M['NonCurrentDeferredAssets']) + n(BS3M['TotalNonCurrentAssets']) - (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))) * ((n(inflationTableValue)) / n(inflationTableAge))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetFixedCapitalExcl: ((n(BS3M['NetPPE']) + n(BS3M['NonCurrentDeferredAssets']) + n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances)) * (n(inflationTableValue)) / n(inflationTableAge)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetFixedCapitalExclGrw: inflAdjNetFixedCapitalGrw === null ? "0" : (inflAdjNetFixedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          InflAdjGrossInvestedCapitalIncl: (grossInvestedCapitalIncl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          InflAdjGrossInvestedCapitalExcl: (grossInvestedCapitalExcl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjGrossInvestedCapitalExclGrw: grossInvestedCapitalGrw === null ? "0" : (grossInvestedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          InflAdjNetInvestedCapitalIncl: (((n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + n(BS3M['NonCurrentDeferredAssets']) + n(BS3M['TotalNonCurrentAssets']) - (n(BS3M['NetPPE']) + n(BS3M['Goodwill']) + n(BS3M['NonCurrentDeferredAssets']) + (n(BS3M['GoodwillAndOtherIntangibleAssets']) - n(BS3M['Goodwill'])) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))) + (n(BS3M['InvestmentsAndAdvances']) + n(BS3M['DeferredCosts']) + n(BS3M['NetLoan']) + n(BS3M['DefinedPensionBenefit']) + n(BS3M['FinancialAssets']))) * ((n(inflationTableValue)) / n(inflationTableAge))) 
          + (n(inflationTableValue)) / n(inflationTableAge) + (n(derived.totalCurrentAssets) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable']) + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']) + n(derived.otherMiscLiabilities)))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetInvestedCapitalExcl: (((n(BS3M['NetPPE']) + n(BS3M['NonCurrentDeferredAssets']) + n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances)) * (n(inflationTableValue)) / n(inflationTableAge)) + (n(derived.totalCurrentAssets) - (n(BS3M['AccountsPayable']) + n(BS3M['DividendsPayable']) + n(BS3M['CurrentNotesPayable'])
          + n(BS3M['CurrentAccruedExpenses']) + n(BS3M['CurrentDeferredLiabilities']) + n(BS3M['TotalTaxPayable']) + n(BS3M['OtherCurrentLiabilities']) + n(derived.otherMiscLiabilities)))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetInvestedCapitalExclGrw: netInvestedCapitalGrw === null ? "0" : (netInvestedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          plowback: n(plowbackRatio).toFixed(1) + "%",
          price: n(prices[priceDate]).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          marketCap: (n(ER3M['BasicAverageShares'] / 1000000) * n(prices[priceDate])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          enterpriseValue: ((n(ER3M['BasicAverageShares'] / 1000000) * n(prices[priceDate])) + (n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) +  n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + n(derived.nonCurrentMiscLiabilities) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) - n(BS3M['NonCurrentDeferredLiabilities']) - n(BS3M['NonCurrentAccruedExpenses']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          evOverEbitda: ebitdaSum === 0 ? "N/A" : (((n(ER3M['BasicAverageShares'] / 1000000) * n(prices[priceDate])) + (n(BS3M['CurrentDebtAndCapitalLeaseObligation']) + n(BS3M['LongTermDebt']) + n(BS3M['LongTermCapitalLeaseObligation']) + n(BS3M['NonCurrentDeferredLiabilities']) + n(BS3M['NonCurrentAccruedExpenses']) +  n(BS3M['DerivativeProductLiabilities']) + n(BS3M['EmployeeBenefits']) + n(BS3M['LongTermProvisions']) + n(BS3M['RegulatoryLiabilities']) + n(BS3M['OtherNonCurrentLiabilities']) + n(derived.nonCurrentMiscLiabilities) + n(BS3M['MinorityInterest']) + n(BS3M['PreferredStockOfSubsidiary']) + n(BS3M['PreferredSecuritiesOutsideStockEquity']) + n(BS3M['PreferredStockEquity']) - n(BS3M['NonCurrentDeferredLiabilities']) - n(BS3M['NonCurrentAccruedExpenses']))) / (ebitdaSum)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "x",

        //Asset Life
          nonCurrentAssetsLife: (n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          depreciatingAssetLife: (n(grossFixedAssets) + n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances) + n(BS3M['Inventory'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          data: 1,
          assetLifeAnnual: Math.min(n(assetLife), 30).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLife3Year: (averageAssetLife).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLifeExclIntangible: (Math.min(30, averageAssetLife)).toFixed(0),
          grossFixedAssetsInclIntangible: (grossFixedAssets + n(BS3M['Goodwill']) + n(derived.otherIntangibleAssets)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          depreciatingAssetLifeInclIntangible: (n(grossFixedAssets) + n(BS3M['Goodwill']) + n(derived.otherIntangibleAssets) + (n(derived.otherNonCurrentAssets) + n(derived.investmentsAndAdvances)) + n(BS3M['Inventory'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLifeAnnualInclIntangible: Math.min(n(assetLifeInclIntangibles), 30).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLife3YearInclIntangible: (averageAssetLifeInclIntangible).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLifeInclIntangible: (Math.min(30, averageAssetLifeInclIntangible)).toFixed(0),
        //Salvage Value
          totalCurrentLiabilitiesMinusShortTermDebt: (n(derived.totalCurrentLiabilities) - n(BS3M['CurrentDebtAndCapitalLeaseObligation'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          salvageValue: (salvageValue).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          salvageValuePercent: ((salvageValue / grossInvestedCapitalExcl) * 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
        
        //CFIRR
          CFIRRInclTFQ : (cfirrTFQIncl * 100).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          CFIRRExclTFQ : (cfirrTFQExcl * 100).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          CFIRRInclQ : (cfirrAnnualQIncl * 100).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          CFIRRExclQ : (cfirrAnnualQExcl * 100).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",

        //Dividends
          cashFlowPerShare: cashFlowPerShare.toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          cashAvailable: cashAvailable.toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          tfqDividends: tfqDividends.toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          payoutRatio: payoutRatio.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          dividendYield: dividendYield.toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          dividendGrowth: dividendGrowth.toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        });

        // // Annual Rows
        // rows.push({
        //   Date: IS12M['AsOfDate'],
        // });
        
        CFPrev.push(cashFlowFromOps);
        if (CFPrev.length > 3) CFPrev.shift();

        CF2Prev.push(cashFlowFromOps2);
        if (CF2Prev.length > 4) CF2Prev.shift();
    }

  return rows;
}




// ---------------- Financial Summary Calculation ----------------
export async function calculateAnnualFinancialSummary(newcapRD, newincludeOtherIncome, newselectedTax) {

  const capRD = newcapRD;
  const includeOtherIncome = newincludeOtherIncome;
  const selectedTax = newselectedTax;

  const incomeStatement3M = await getTableData("Income Statement 3M");
  const balanceSheet3M = await getTableData("Balance Sheet 3M");
  const earningReports3M = await getTableData("Earnings Reports 3M");
  const incomeStatement12M = await getTableData("Income Statement 12M");
  const balanceSheet12M = await getTableData("Balance Sheet 12M");
  const earningReports12M = await getTableData("Earnings Reports 12M");
  const ratesTable = await getTableData("Rates");
  const PVLongTerm = await getTableData("PVLongTerm");
  const QuarterlyPrices = await getTableData("QuarterlyPrice");
  const rates = ratesTable[0];
  const rawPrice = QuarterlyPrices[1];
  const exchangeRate = QuarterlyPrices[3];

  const prices = fillLeadingZerosWithFirstValue(rawPrice);
  

  if (!incomeStatement3M || !balanceSheet3M || !earningReports3M || !incomeStatement12M) return [];

  const rows = [];
  //helper variables
  let aDepPrev = 0;
  let naCAPrev = 0;
  let CFPrev = [0, 0, 0];
  let CF2Prev = [0, 0, 0, 0];
  let numOfPeriods = 0;
  let sharesArray = [];
  let assetLifeArray = [];
  let depreciationArray = [];
  let assetLife = 0;
  let assetLifeInclIntangibles = 0;
  let averageAssetLife = 0;
  let averageAssetLifeInclIntangible = 0;
  let annualAssetLifeArray = [];
  let annualCapitalExcl = [];
  let assetLifeArrayInclIntangible = [];
  let annualAssetLifeArrayInclIntangible = [];
  let annualCapitalIncl = [];
  let netFixedCapitalArray = [];
  let InflAdjGrossFixedCapitalArray = [];
  let InflAdjNetFixedCapitalArray = [];
  let grossInvestedCapitalExclArray = [];
  let grossInvestedCapitalExclArray2 = [];
  let grossInvestedCapitalInclArray = [];
  let netInvestedCapitalArray = [];
  let inflationTableValue = 0;
  let salvageValueArray = [];
  let salvageValueArray2 = [];
  let cashFlowArray = [];
  let interestExpenseArray = [];
  let plowbackNumerator = [];
  let plowbackDenominator = [];
  //CFIRR Variables
  let cfirrTFQIncl = 0;
  let cfirrTFQExcl = 0;
  let cfirrAnnualQIncl = 0;
  let cfirrAnnualQExcl = 0;
  let cashFlowPerShareArray = [];
  let cashFlowPerShareArray2 = [];
  let tfqDividendsArray = [];
  let tfqDividends = 0;
  let cashAvailable = 0;
  let payoutRatio = 0;
  let dividendGrowth = 0;
  let totalRevenueArray = [];
  let totalRevenueGrw = 0;
  let ebitdaArray = [];
  let ebitdaGrw = 0;
  let ebitdaSum = 0;
  //----------------------------------

  for (let i = 0; i < incomeStatement12M.length; i++) {
    const IS12 = incomeStatement12M[i], BS12 = balanceSheet12M[i], ER12 = earningReports12M[i];
    
    if (!IS12 || IS12['AsOfDate'] === 0) continue; // skip if undefined

    naCAPrev = n(IS12['ResearchAndDevelopment']) + naCAPrev;
    // --- Period counter ---
    if (numOfPeriods < 3 && IS12['AsOfDate'] !== 0) numOfPeriods++;

    // Variables
    const costOfSales = n(IS12['CostOfRevenue']) + n(IS12['ReconciledDepreciation']);
    const grossMarginIncl = n(IS12['TotalRevenue']) - (n(IS12['CostOfRevenue']) + n(IS12['ReconciledDepreciation']));
    const grossMarginExcl = n(IS12['TotalRevenue']) - n(IS12['CostOfRevenue']);
    const generalAdmin = generalAndAdmin(IS12, IS12);
    const otherIncome = n(IS12['OtherIncomeExpense']) - n(IS12['SpecialIncomeCharges']) - n(IS12['RestructuringAndMergernAcquisition']);
    
    const operatingIncome = 
        grossMarginExcl
        - n(generalAdmin)
        - n(IS12['ResearchAndDevelopment'])
        + (includeOtherIncome ? otherIncome : 0)

    const ebitda = 
        grossMarginExcl - 
        generalAdmin - 
        (capRD ? 0 : n(IS12['ResearchAndDevelopment'])) +
        n(IS12['EarningsFromEquityInterest']) +
        n(IS12['ReconciledDepreciation']) +
        (includeOtherIncome ? otherIncome : 0);

    const ebit = 
        n(ebitda) -
        n(IS12['ReconciledDepreciation']);

    const specialItems =
      n(IS12['RestructuringAndMergernAcquisition']) +
      n(IS12['AcquiredInProcessRnDIncome']) +
      n(IS12['SpecialIncomeCharges']);

    const otherIncomeAdj =
      n(IS12['OtherIncomeExpense']) -
      n(IS12['SpecialIncomeCharges']) -
      n(IS12['RestructuringAndMergernAcquisition']);

    const netIncome =
      (
        n(ebitda) -
        n(IS12['ReconciledDepreciation']) +
        n(IS12['InterestIncome']) +
        specialItems +
        otherIncomeAdj -
        n(IS12['InterestExpense']) -
        n(IS12['EarningsFromEquityInterest']) -
        n(IS12['TaxProvision'])
      ) +
        n(IS12['NetIncomeDiscontinuousOperations']) +
        n(IS12['NetIncomeExtraordinary']) +
        n(IS12['CumulativeEffectOfAccountingChange']) +
        n(IS12['NetIncomeFromTaxLossCarryforward']) +
        n(IS12['OtherOperatingExpenses']) +
        n(IS12['MinorityInterests']);

    const otherCurrentAssets = 
      n(BS12['CurrentAssets']) - 
      ((Math.max(n(BS12['AccountsReceivable']), n(BS12['Receivables']))) + 
      n(BS12['Inventory']) + n(BS12['PrepaidAssets']) + 
      n(BS12['CurrentDeferredTaxesAssets']) + n(BS12['CashAndCashEquivalents']) + 
      n(BS12['RestrictedCash']) + n(BS12['OtherShortTermInvestments']));

    const totalCurrentAssets = 
      (n(BS12['CurrentAssets']) - 
      ((Math.max(n(BS12['AccountsReceivable']), n(BS12['Receivables']))) + 
      n(BS12['Inventory']) + n(BS12['PrepaidAssets']) + 
      n(BS12['CurrentDeferredTaxesAssets']) + n(BS12['CashAndCashEquivalents']) + 
      n(BS12['RestrictedCash']) + n(BS12['OtherShortTermInvestments']))) + 
      n(BS12['CashAndCashEquivalents']) + n(BS12['RestrictedCash']) + n(BS12['OtherShortTermInvestments']) + 
      (Math.max(n(BS12['AccountsReceivable']), n(BS12['Receivables']))) + n(BS12['Inventory']) + n(BS12['PrepaidAssets']) + n(BS12['CurrentDeferredTaxesAssets']);
          
    const accumulatedDep = n(accumulatedDepreciation(BS12, IS12, aDepPrev));

    const otherIntangibleAssets = n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill']);

    const otherNonCurrentAssets = 
      n(BS12['TotalNonCurrentAssets']) - 
      (n(BS12['NetPPE']) + n(BS12['Goodwill']) + 
      n(BS12['NonCurrentDeferredAssets']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - 
      n(BS12['Goodwill'])) + (n(BS12['InvestmentsAndAdvances']) + 
      n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets'])));
          
    const investmentsAndAdvances = 
      n(BS12['InvestmentsAndAdvances']) + 
      n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + 
      n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']);
          
    const totalNonCurrentAssets = 
      n(BS12['NetPPE']) + n(BS12['Goodwill']) + 
      (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + 
      n(BS12['NonCurrentDeferredAssets']) + 
      (n(BS12['TotalNonCurrentAssets']) - (n(BS12['NetPPE']) + n(BS12['Goodwill']) + 
      n(BS12['NonCurrentDeferredAssets']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + 
      (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets'])))) + 
      (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']));
          
    const otherMiscLiabilities = 
      n(BS12['CurrentLiabilities']) - 
      (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable']) + 
      n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['CurrentAccruedExpenses']) + 
      n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']));

    const totalCurrentLiabilities = 
      n(BS12['AccountsPayable']) + 
      n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable']) + 
      n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['CurrentAccruedExpenses']) + 
      n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + 
      (n(BS12['CurrentLiabilities']) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + 
      n(BS12['CurrentNotesPayable']) + n(BS12['CurrentDebtAndCapitalLeaseObligation']) + 
      n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + 
      n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities'])));
          
    const nonCurrentMiscLiabilities = 
      n(BS12['TotalNonCurrentLiabilitiesNetMinorityInterest']) - 
      (n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + 
      n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) + 
      n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + 
      n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']));
          
    const totalNonCurrentLiabilities = 
      n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + 
      n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) + 
      n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + 
      n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']) + 
      (n(BS12['TotalNonCurrentLiabilitiesNetMinorityInterest']) - (n(BS12['LongTermDebt']) + 
      n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['NonCurrentDeferredLiabilities']) + 
      n(BS12['NonCurrentAccruedExpenses']) + n(BS12['DerivativeProductLiabilities']) + 
      n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities'])));
          
    const commonStock = n(BS12['CommonStockEquity']) - n(BS12['PreferredStockOfSubsidiary']) - n(BS12['PreferredSecuritiesOutsideStockEquity']);
          
    const totalOwnerEquity = 
      n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + 
      n(BS12['PreferredSecuritiesOutsideStockEquity']) + n(BS12['PreferredStockEquity']) + 
      (n(BS12['CommonStockEquity']) - n(BS12['PreferredStockOfSubsidiary']) - n(BS12['PreferredSecuritiesOutsideStockEquity']));        

    const netPPE = n(BS12['NetPPE']);
    const grossPPE = n(BS12['GrossPPE']);

    const grossFixedAssets =
      Math.max((netPPE + accumulatedDep), grossPPE) +
      ((capRD ? naCAPrev : 0))

    // Get net working liabilities
    const currentLiabilities =
        n(BS12['AccountsPayable']) +
        n(BS12['DividendsPayable']) +
        n(BS12['CurrentNotesPayable']) +
        n(BS12['CurrentAccruedExpenses']) +
        n(BS12['CurrentDeferredLiabilities']) +
        n(BS12['TotalTaxPayable']) +
        n(BS12['OtherCurrentLiabilities']) +
        n(otherMiscLiabilities);

    //CFandIncome
    const nonOperationIncome = 
      n(IS12['InterestIncome']) + n(IS12['RestructuringAndMergernAcquisition']) + 
      n(IS12['AcquiredInProcessRnDIncome']) + n(IS12['SpecialIncomeCharges']) + 
      (otherIncome);

    const nonOperationIncomeAT = 
      0.72 * (n(IS12['InterestIncome']) + 
      n(IS12['RestructuringAndMergernAcquisition']) + n(IS12['AcquiredInProcessRnDIncome']) + n(IS12['SpecialIncomeCharges'])) + 
      (otherIncome);

    const totalOtherIncome = 
      n(IS12['NetIncomeDiscontinuousOperations']) + 
      n(IS12['NetIncomeExtraordinary']) + n(IS12['CumulativeEffectOfAccountingChange']) + 
      n(IS12['NetIncomeFromTaxLossCarryforward']) + n(IS12['OtherOperatingExpenses']);
    
    const userTax = getTaxRate(IS12, selectedTax, IS12, IS12, includeOtherIncome, capRD);
  
    //Invested Capital
    const totalOtherFixedCapital = 
      n(BS12['Goodwill']) + 
      (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + 
      n(BS12['NonCurrentDeferredAssets']) + n(BS12['TotalNonCurrentAssets']) - 
      (n(BS12['NetPPE']) + n(BS12['Goodwill']) + n(BS12['NonCurrentDeferredAssets']) + 
      (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + 
      (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + 
      n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))) + 
      (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']));

    const investedCapitalFinancing = 
      n(BS12['CurrentDebtAndCapitalLeaseObligation']) + 
      n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + 
      n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) + 
      n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + 
      n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + 
      n(BS12['OtherNonCurrentLiabilities']) + (n(BS12['CurrentLiabilities']) - 
      (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable']) + 
      n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['CurrentAccruedExpenses']) + 
      n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']))) + 
      n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + 
      n(BS12['PreferredStockEquity']) + (n(BS12['CommonStockEquity']) - n(BS12['PreferredStockOfSubsidiary']) - n(BS12['PreferredSecuritiesOutsideStockEquity']));
    
    const debtAndEquivalents = 
      n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['LongTermDebt']) + 
      n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['DerivativeProductLiabilities']) + 
      n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + 
      n(BS12['OtherNonCurrentLiabilities']) + (n(BS12['CurrentLiabilities']) - 
      (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable']) + 
      n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['CurrentAccruedExpenses']) + 
      n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']))) + 
      n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + 
      n(BS12['PreferredStockEquity']) + (n(BS12['CommonStockEquity']) - n(BS12['PreferredStockOfSubsidiary']) - n(BS12['PreferredSecuritiesOutsideStockEquity']));
    
    // --- Cash Flow from Ops ---
    const cashFlowFromOps =
        n(netIncome) -
        n(nonOperationIncome) -
        n(totalOtherIncome) +
        0.72 * n(IS12['InterestExpense']) +
        n(IS12['MinorityInterests']) +
        n(IS12['ReconciledDepreciation']) -
        (n(userTax) - n(IS12['TaxProvision']));

    const cashFlowFromOps2 =
        n(ebitda) - n(userTax) - 0.28 * n(IS12['InterestExpense']);

    aDepPrev = accumulatedDep;
    
    // --- Shares ---
      sharesArray.push(n(BS12['OrdinarySharesNumber']));
      const sharesRatio = Average(sharesArray);

      if (sharesArray.length === 2) sharesArray.shift();

        // --- Asset Life ---

        assetLifeArray.push(
            grossFixedAssets +
            n(otherNonCurrentAssets) +
            n(investmentsAndAdvances) +
            n(BS12['Inventory'])
        );

        depreciationArray.push(n(IS12['ReconciledDepreciation']));

        assetLifeArrayInclIntangible.push(
            grossFixedAssets + 
            n(BS12['Goodwill']) + 
            n(otherIntangibleAssets) + 
            (n(otherNonCurrentAssets) + 
            n(investmentsAndAdvances)) + 
            n(BS12['Inventory'])
        );

        // console.log((grossFixedAssets +
        //     n(otherNonCurrentAssets) +
        //     n(investmentsAndAdvances) +
        //     n(BS12['Inventory'])) / n(IS12['ReconciledDepreciation']))

        // if (numOfPeriods === 1) {
        //   assetLife = (Math.min((grossFixedAssets +
        //     n(otherNonCurrentAssets) +
        //     n(investmentsAndAdvances) +
        //     n(BS12['Inventory'])) / n(IS12['ReconciledDepreciation']), 30));
          
        //   assetLifeInclIntangibles = (Math.min(n(grossFixedAssets + 
        //     n(BS12['Goodwill']) + 
        //     n(otherIntangibleAssets) + 
        //     (n(otherNonCurrentAssets) + 
        //     n(investmentsAndAdvances)) + 
        //     n(BS12['Inventory'])) / n(IS12['ReconciledDepreciation']), 30));

        //   averageAssetLife = assetLife;
        //   averageAssetLifeInclIntangible = assetLifeInclIntangibles;
        // } else {
        //   assetLife = (Math.min(n(Average(assetLifeArray) / n(IS12['ReconciledDepreciation'])), 30));
        //   assetLifeInclIntangibles = (Math.min(n(Average(assetLifeArrayInclIntangible) / n(IS12['ReconciledDepreciation'])), 30));

        // }

        assetLife = (Math.min(n(Average(assetLifeArray) / n(IS12['ReconciledDepreciation'])), 30));
        assetLifeInclIntangibles = (Math.min(n(Average(assetLifeArrayInclIntangible) / n(IS12['ReconciledDepreciation'])), 30));


        annualAssetLifeArray.push(assetLife);
        annualAssetLifeArrayInclIntangible.push(assetLifeInclIntangibles);


        averageAssetLife = Average(annualAssetLifeArray);
        averageAssetLifeInclIntangible = Average(annualAssetLifeArrayInclIntangible);

        if (annualAssetLifeArray.length === 3) {
          annualAssetLifeArray.shift();
        }
        if (annualAssetLifeArrayInclIntangible.length === 3) {
          annualAssetLifeArrayInclIntangible.shift();
        }


        // --- Inflation ---

        const yearIndex =
          Math.ceil(
            Math.min(30, averageAssetLife) *
              (1 - netPPE / (netPPE + accumulatedDep))
          ) * 365;

        const inflationAge = new Date(IS12['AsOfDate']).getTime() - yearIndex * 86400 * 1000;
        const inflationDate = findClosestDate(new Date(IS12['AsOfDate']).getTime(), rates);
        const inflationAgeDate = findClosestDate(new Date(inflationAge).getTime(), rates);

        const inflationTableAge = rates[inflationAgeDate];

        if (rates[IS12['AsOfDate']] !== undefined) {
          inflationTableValue = n(rates[IS12['AsOfDate']]);
        } else {
          inflationTableValue = rates[inflationDate];
        }

        const inflAdjGrossFixedCapitalIncl = (n(totalOtherFixedCapital) + n(grossFixedAssets)) * ((n(inflationTableValue)) / n(inflationTableAge));
        const inflAdjGrossFixedCapitalExcl = (n(totalOtherFixedCapital) - n(BS12['Goodwill']) - (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + n(grossFixedAssets)) * ((n(inflationTableValue)) / n(inflationTableAge));

        // --- Price ---
        const rawPriceDate = new Date(IS12['AsOfDate']);
        rawPriceDate.setDate(rawPriceDate.getDate() + 45);
        const priceDate = findClosestDate(new Date(rawPriceDate).getTime(), prices);

        // --- Growth Rate Values ---

        const grossInvestedCapitalIncl =
          (inflAdjGrossFixedCapitalIncl) +
          Math.max(n(totalCurrentAssets) - currentLiabilities, 0);

        const grossInvestedCapitalExcl =
          (inflAdjGrossFixedCapitalExcl) +
          Math.max(n(totalCurrentAssets) - currentLiabilities, 0);

        netFixedCapitalArray.push(
          netPPE + n(BS12['NonCurrentDeferredAssets']) + n(otherNonCurrentAssets) + n(investmentsAndAdvances)
        );

        InflAdjGrossFixedCapitalArray.push(inflAdjGrossFixedCapitalExcl);

        InflAdjNetFixedCapitalArray.push(
          (n(BS12['NetPPE']) + 
          n(BS12['NonCurrentDeferredAssets']) + 
          n(otherNonCurrentAssets) + 
          n(investmentsAndAdvances)) * 
          (n(inflationTableValue)) / n(inflationTableAge));

        grossInvestedCapitalExclArray.push(grossInvestedCapitalExcl);

        netInvestedCapitalArray.push(
          (((n(BS12['NetPPE']) + n(BS12['NonCurrentDeferredAssets']) + n(otherNonCurrentAssets) + n(investmentsAndAdvances)) * (n(inflationTableValue)) / n(inflationTableAge)) + (n(totalCurrentAssets) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable'])
          + n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + n(otherMiscLiabilities))))
        );


        // --- EBITDA Growth ---
        ebitdaArray.push(n(ebitda));
        const ebitdaMargin = n(ebitda) / n(IS12['TotalRevenue']) * 100;
        if (ebitdaArray.length > 1) {
          ebitdaSum = ebitdaArray.reduce((a, b) => a + b, 0);
          ebitdaGrw = ((ebitdaArray.at(-1) / ebitdaArray.at(-2)) - 1) * 100;
          ebitdaArray.shift();
        }

        // Growth Rates
          const netFixedCapitalGrw = growthRate(netFixedCapitalArray.at(-1), netFixedCapitalArray.at(-2));
          const inflAdjGrossFixedCapitalGrw = growthRate(InflAdjGrossFixedCapitalArray.at(-1), InflAdjGrossFixedCapitalArray.at(-2));
          const inflAdjNetFixedCapitalGrw = growthRate(InflAdjNetFixedCapitalArray.at(-1), InflAdjNetFixedCapitalArray.at(-2));
          const grossInvestedCapitalGrw = growthRate(grossInvestedCapitalExclArray.at(-1), grossInvestedCapitalExclArray.at(-2));
          const netInvestedCapitalGrw = growthRate(netInvestedCapitalArray.at(-1), netInvestedCapitalArray.at(-2));

        // --- CFIRR ---
        cashFlowArray.push(cashFlowFromOps);

        const salvageValue = Math.max(
          0,
          n(totalCurrentAssets) -
            (n(totalCurrentLiabilities) - n(BS12['CurrentDebtAndCapitalLeaseObligation']))
        );

        salvageValueArray.push(salvageValue);
        salvageValueArray2.push(salvageValue);

        const cashFlowSum = cashFlowArray.reduce((a, b) => a + b, 0);
        const salvageValueAvg = Average(salvageValueArray);

        grossInvestedCapitalInclArray.push(grossInvestedCapitalIncl);
        grossInvestedCapitalExclArray2.push(grossInvestedCapitalExcl);
        annualCapitalIncl.push(grossInvestedCapitalIncl);
        annualCapitalExcl.push(grossInvestedCapitalExcl);

        // Quarterly CFIRR
        if (cashFlowArray.length > 1) {
          const avgSalvageValue = Average(salvageValueArray2);
          const pvIncl = Average(annualCapitalIncl) * -1;
          const pvExcl = Average(annualCapitalExcl) * -1;

          //cfirrAnnualQExcl = CFIRR(assetLife, cashFlowFromOps * 4, pvExcl, avgSalvageValue, 0, 0.07);
         // cfirrAnnualQIncl = CFIRR(assetLifeInclIntangibles, cashFlowFromOps * 4, pvIncl, avgSalvageValue, 0, 0.07);

          annualCapitalExcl.shift();
          annualCapitalIncl.shift();
          salvageValueArray2.shift();
        }

        // TFQ CFIRR
          const pvValueIncl = Average(grossInvestedCapitalInclArray) * -1;
          const pvValueExcl = Average(grossInvestedCapitalExclArray2) * -1;

          cfirrTFQExcl = CFIRR(averageAssetLife, cashFlowFromOps, pvValueExcl, salvageValueAvg, 0, 0.07);
          cfirrTFQIncl = CFIRR(averageAssetLifeInclIntangible, cashFlowFromOps, pvValueIncl, salvageValueAvg, 0, 0.07);


        if (cashFlowArray.length > 1) {
            salvageValueArray.shift();
            assetLifeArray.shift();
            assetLifeArrayInclIntangible.shift();
            grossInvestedCapitalInclArray.shift();
            grossInvestedCapitalExclArray2.shift();
            // annualAssetLifeArray.shift();
            // annualAssetLifeArrayInclIntangible.shift()
        }

        if (cashFlowArray.length === 4) {
          cashFlowArray.shift();
        }

        // --- Plowback ---
        plowbackNumerator.push(cashFlowFromOps);
        plowbackDenominator.push(grossInvestedCapitalExcl);
        interestExpenseArray.push(0.72 * n(IS12['InterestExpense']));

        const plowbackRatio =
          ((plowbackNumerator.at(-2) - interestExpenseArray.at(-2)) / plowbackDenominator.at(-2)) * 100;

        // --- Dividends ---

        // --- Total Revenue Growth ---
        totalRevenueArray.push(n(IS12['TotalRevenue']));
        const totalRevenueGrw = ((totalRevenueArray.at(-1) / totalRevenueArray.at(-2)) - 1);


    rows.push({
      Time: "Annual",
      Date: IS12['AsOfDate'],
      operatingRevenue: n(IS12['OperatingRevenue']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherTaxes: n(IS12['OtherTaxes']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalRevenue: n(IS12['TotalRevenue']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalRevenueGrw: (i === 0 ? 0 : totalRevenueGrw).toLocaleString("en-US", { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          costOfRevenue: n(IS12['CostOfRevenue']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          costOfSales: n(costOfSales).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          grossMarginIncl: n(grossMarginIncl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          grossMarginExcl: n(grossMarginExcl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          generalAndAdmin: n(generalAdmin).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          researchAndDevelopment: n(IS12['ResearchAndDevelopment']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherIncome: n(otherIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          operatingIncome: n(operatingIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          equityInterest: n(IS12['EarningsFromEquityInterest']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          ebitda: n(ebitda).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          ebitdaMargin: (n(ebitdaMargin)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          ebitdaGrw: (n(ebitdaGrw)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          depreciation: n(IS12['ReconciledDepreciation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          ebit: (n(ebit).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })),
          interestIncome: n(IS12['InterestIncome']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeRest: n(IS12['RestructuringAndMergernAcquisition']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeAcquired: n(IS12['AcquiredInProcessRnDIncome']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          specialIncome: n(IS12['SpecialIncomeCharges']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeAvail: (n(ebitda) - n(IS12['ReconciledDepreciation']) + n(IS12['InterestIncome']) + n(IS12['RestructuringAndMergernAcquisition']) + n(IS12['AcquiredInProcessRnDIncome']) + n(IS12['SpecialIncomeCharges']) + ((includeOtherIncome ? (n(IS12['OtherIncomeExpense']) - n(IS12['SpecialIncomeCharges']) - n(IS12['RestructuringAndMergernAcquisition'])) : 0))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          interestExpense: n(IS12['InterestExpense']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          beforeTaxIncome: ((n(ebitda) - n(IS12['ReconciledDepreciation']) + n(IS12['InterestIncome']) + n(IS12['RestructuringAndMergernAcquisition']) + n(IS12['AcquiredInProcessRnDIncome']) + n(IS12['SpecialIncomeCharges']) + (n(IS12['OtherIncomeExpense']) - n(IS12['SpecialIncomeCharges']) - n(IS12['RestructuringAndMergernAcquisition']))) - n(IS12['InterestExpense']) - n(IS12['EarningsFromEquityInterest'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          incomeTax: n(IS12['TaxProvision']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromCO: (((n(ebitda) - n(IS12['ReconciledDepreciation']) + n(IS12['InterestIncome']) + n(IS12['RestructuringAndMergernAcquisition']) + n(IS12['AcquiredInProcessRnDIncome']) + n(IS12['SpecialIncomeCharges']) + (n(IS12['OtherIncomeExpense']) - n(IS12['SpecialIncomeCharges']) - n(IS12['RestructuringAndMergernAcquisition']))) - n(IS12['InterestExpense']) - n(IS12['EarningsFromEquityInterest'])) - IS12['TaxProvision']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromDO: n(IS12['NetIncomeDiscontinuousOperations']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromEI: n(IS12['NetIncomeExtraordinary']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromAC: n(IS12['CumulativeEffectOfAccountingChange']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromTLC: n(IS12['NetIncomeFromTaxLossCarryforward']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeFromOG: n(IS12['OtherOperatingExpenses']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          minorityInterests: n(IS12['MinorityInterests']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncome: netIncome.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          preferredDividends: n(IS12['PreferredStockDividends']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netIncomeAvailable: (netIncome - n(IS12['PreferredStockDividends'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          sharesOutstanding: n(BS12['OrdinarySharesNumber']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsContinuingOps: n(ER12['BasicContinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsDiscontinuedOps: n(ER12['BasicDiscontinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsExtraordinaryINC: n(ER12['BasicExtraordinary']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsAccountingChg: n(ER12['BasicAccountingChange']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsOtherGains: n(ER12['BasicEPSOtherGainsLosses']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicEpsTotal: n(ER12['BasicEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          normalizedBasicEps: n(ER12['NormalizedBasicEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          basicAverageShares: n(ER12['BasicAverageShares'] / 1000000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsContinuingOps: n(ER12['DilutedContinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsDiscontinuedOps: n(ER12['DilutedDiscontinuousOperations']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsExtraordinaryINC: n(ER12['DilutedExtraordinary']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsAccountingChg: n(ER12['DilutedAccountingChange']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsOtherGains: n(ER12['DilutedEPSOtherGainsLosses']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedEpsTotal: n(ER12['DilutedEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          normalizedDilutedEps: n(ER12['NormalizedDilutedEPS']).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dilutedAverageShares: n(ER12['DilutedAverageShares'] / 1000000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dividendPerShare: n(ER12['DividendPerShare']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          // Balance Sheet Values
          // Assets
          cashAndEquivalents: n(BS12['CashAndCashEquivalents']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          restrictedCash: n(BS12['RestrictedCash']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          marketableSecurities: n(BS12['OtherShortTermInvestments']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          accountsReceivable: Math.max(n(BS12['AccountsReceivable']), n(BS12['Receivables'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          inventory: n(BS12['Inventory']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          prepaidExpenses: n(BS12['PrepaidAssets']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          currentDeferredAssets: n(BS12['CurrentDeferredTaxesAssets']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherCurrentAssets: n(otherCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalCurrentAssets: n(totalCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentAssets: (naCAPrev).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          accumulatedDepreciation: n(accumulatedDep).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          grossFixedAssets: n(grossFixedAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netPremises: n(BS12['NetPPE']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          goodwill: n(BS12['Goodwill']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherIntangibleAssets: n(otherIntangibleAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentDeferredAssets: n(BS12['NonCurrentDeferredAssets']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherNonCurrentAssets: n(otherNonCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          investmentsAndAdvances: n(investmentsAndAdvances).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalNonCurrentAssets: n(totalNonCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalAssets: n(totalCurrentAssets + totalNonCurrentAssets).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          // Liabilities
          accountsPayable: n(BS12['AccountsPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          dividendsPayable: n(BS12['DividendsPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          notesPayable: n(BS12['CurrentNotesPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          shortTermDebt: n(BS12['CurrentDebtAndCapitalLeaseObligation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          accruedExpenses: n(BS12['CurrentAccruedExpenses']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          deferredRevenue: n(BS12['CurrentDeferredLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          currentDeferredIncomeTaxes: n(BS12['TotalTaxPayable']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherCurrentLiabilities: n(BS12['OtherCurrentLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherMiscLiabilities: n(otherMiscLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalCurrentLiabilities: n(totalCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          longTermDebt: n(BS12['LongTermDebt']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          capitalLeaseObligations: n(BS12['LongTermCapitalLeaseObligation']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          deferredIncomeTaxes: n(BS12['NonCurrentDeferredLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherDeferredTaxAssets: n(BS12['NonCurrentAccruedExpenses']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          derivativeLiabilities: n(BS12['DerivativeProductLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          retirementBenefits: n(BS12['EmployeeBenefits']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          longTermProvisions: n(BS12['LongTermProvisions']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          regulatoryLiabilities: n(BS12['RegulatoryLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentOtherLiabilities: n(BS12['OtherNonCurrentLiabilities']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCurrentMiscLiabilities: n(nonCurrentMiscLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalNonCurrentLiabilities: n(totalNonCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalLiabilities: n(totalCurrentLiabilities + totalNonCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          // Equity
          minorityInterestsBal: n(BS12['MinorityInterest']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          preferredSecurities: n(BS12['PreferredStockOfSubsidiary']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          outsideStockEquity: n(BS12['PreferredSecuritiesOutsideStockEquity']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          stockEquity: n(BS12['PreferredStockEquity']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          commonStock: n(commonStock).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOwnerEquity: n(totalOwnerEquity).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalLiabilitiesAndEquity: n(totalCurrentLiabilities + totalNonCurrentLiabilities + totalOwnerEquity).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          additionalPaidInCapital: n(BS12['AdditionalPaidInCapital']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          retainedEarnings: n(BS12['RetainedEarnings']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          treasuryStock: (n(BS12['TreasuryStock']) * -1).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          otherComprehensiveIncome: n(BS12['GainsLossesNotAffectingRetainedEarnings']).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),

          //CF and Income
          taxRate: "28%",
          nonOperationIncome: n(nonOperationIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonOperationIncomeAT: n(nonOperationIncomeAT).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherIncome: n(totalOtherIncome).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          interestExpenseAT: (0.72 * n(IS12['InterestExpense'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          calculatedTax: ((n(ebitda - IS12['ReconciledDepreciation']) - n(IS12['InterestExpense'])) * 0.28).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          chosenTax: n(userTax).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cashFlowFromOps: (cashFlowFromOps).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          taxShield: (0.28 * n(IS12['InterestExpense'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cashFlowFromOps12M: (cashFlowFromOps).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cashFlowFromOpsAnnualized: ("").toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          secondCashFlow: (ebitda - userTax - (0.28 * n(IS12['InterestExpense']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          secondCashFlow12M: (ebitda - userTax - (0.28 * n(IS12['InterestExpense']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          secondCashFlowAnnualized: ("").toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          numOfPeriods: numOfPeriods,
          ebitdaPerShare: sharesRatio === 0 ? "N/A" : "$" + (ebitda / n(sharesRatio) || 0).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cfFromOpsPerShare: sharesRatio === 0 ? "N/A" : "$" + ((ebitda - userTax - (0.28 * n(IS12['InterestExpense']))) / n(sharesRatio)).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cfInterest: (cashFlowFromOps - (0.72 * n(IS12['InterestExpense']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          cfInterestPerShare: sharesRatio === 0 ? "N/A" : "$" + ((cashFlowFromOps - (0.72 * n(IS12['InterestExpense']))) / n(sharesRatio)).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),

        //Invested Capital
          // Working capital
          netWorkingCapital: n(totalCurrentAssets - totalCurrentLiabilities).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),

          netWorkingCapitalExclDebt: (n(totalCurrentAssets) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable'])
          + n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + n(otherMiscLiabilities))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          nonCashWorkingCapital: n(n(Math.max(n(BS12['AccountsReceivable']), n(BS12['Receivables']))) + n(BS12['Inventory']) + n(BS12['PrepaidAssets']) + n(BS12['CurrentDeferredTaxesAssets']) + n(otherCurrentAssets)
          - n(BS12['AccountsPayable']) - n(BS12['DividendsPayable']) - n(BS12['CurrentNotesPayable']) - n(BS12['CurrentAccruedExpenses']) - n(BS12['CurrentDeferredLiabilities']) - n(BS12['TotalTaxPayable']) - n(BS12['OtherCurrentLiabilities']) - n(otherMiscLiabilities)),
          //Fixed Capital
          fixedAssets: (n(BS12['NetPPE']) + n(accumulatedDep)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherFixedCapital: n(totalOtherFixedCapital).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherFixedCapitalIncl: (n(totalOtherFixedCapital) + n(grossFixedAssets)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          totalOtherFixedCapitalExcl: (n(totalOtherFixedCapital) - n(BS12['Goodwill']) - (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + n(grossFixedAssets)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netFixedCapitalInclIntangible: (n(BS12['NetPPE']) + n(BS12['Goodwill']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + n(BS12['NonCurrentDeferredAssets']) + n(BS12['TotalNonCurrentAssets']) - (n(BS12['NetPPE']) + n(BS12['Goodwill']) + n(BS12['NonCurrentDeferredAssets']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))) + (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netFixedCapitalExclIntangible: (n(BS12['NetPPE']) + n(BS12['NonCurrentDeferredAssets']) + n(otherNonCurrentAssets) + n(investmentsAndAdvances)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          netFixedCapitalExclIntangibleGrw: netFixedCapitalGrw === null ? "0" : (netFixedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          investedCapitalAsset: (n(totalCurrentAssets) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable'])
          + n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + n(otherMiscLiabilities)) + (n(totalOtherFixedCapital) + n(BS12['NetPPE']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          investedCapitalFinancing: (n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) +  n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']) + n(nonCurrentMiscLiabilities) + n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + n(BS12['PreferredStockEquity']) + n(commonStock)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          investedCapitalDifference: (n(totalCurrentAssets) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable'])
          + n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + n(otherMiscLiabilities)) + (n(totalOtherFixedCapital) + n(BS12['NetPPE'])) - (n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) +  n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']) + n(nonCurrentMiscLiabilities) + n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + n(BS12['PreferredStockEquity']) + n(commonStock))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          debtAndEquivalents: (n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) +  n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']) + n(nonCurrentMiscLiabilities) + n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + n(BS12['PreferredStockEquity']) - n(BS12['NonCurrentDeferredLiabilities']) - n(BS12['NonCurrentAccruedExpenses'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          //Inflation Adjustment
          netPlantOverGrossPlant: ((n(BS12['NetPPE']) / (n(BS12['NetPPE']) + n(accumulatedDep))) * 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          averageAge: (n((Math.min(30, averageAssetLife)).toFixed(0)) * (1 - ((n(BS12['NetPPE']) / (n(BS12['NetPPE']) + n(accumulatedDep)))))),
          inflationTablePeriod: n(inflationTableValue).toFixed(3),
          inflationTableAge: n(inflationTableAge).toFixed(3),
          inflationFactor: ((n(inflationTableValue)) / n(inflationTableAge)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          InflAdjGrossFixedCapitalIncl: (inflAdjGrossFixedCapitalIncl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjGrossFixedCapitalExcl: (inflAdjGrossFixedCapitalExcl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjGrossFixedCapitalExclGrw: inflAdjGrossFixedCapitalGrw === null ? "0" : ((inflAdjGrossFixedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"),
          InflAdjNetFixedCapitalIncl: ((n(BS12['NetPPE']) + n(BS12['Goodwill']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + n(BS12['NonCurrentDeferredAssets']) + n(BS12['TotalNonCurrentAssets']) - (n(BS12['NetPPE']) + n(BS12['Goodwill']) + n(BS12['NonCurrentDeferredAssets']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))) + (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))) * ((n(inflationTableValue)) / n(inflationTableAge))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetFixedCapitalExcl: ((n(BS12['NetPPE']) + n(BS12['NonCurrentDeferredAssets']) + n(otherNonCurrentAssets) + n(investmentsAndAdvances)) * (n(inflationTableValue)) / n(inflationTableAge)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetFixedCapitalExclGrw: inflAdjNetFixedCapitalGrw === null ? "0" : (inflAdjNetFixedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          InflAdjGrossInvestedCapitalIncl: (grossInvestedCapitalIncl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), 
          InflAdjGrossInvestedCapitalExcl: (grossInvestedCapitalExcl).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjGrossInvestedCapitalExclGrw: grossInvestedCapitalGrw === null ? "0" : (grossInvestedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          InflAdjNetInvestedCapitalIncl: (((n(BS12['NetPPE']) + n(BS12['Goodwill']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + n(BS12['NonCurrentDeferredAssets']) + n(BS12['TotalNonCurrentAssets']) - (n(BS12['NetPPE']) + n(BS12['Goodwill']) + n(BS12['NonCurrentDeferredAssets']) + (n(BS12['GoodwillAndOtherIntangibleAssets']) - n(BS12['Goodwill'])) + (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))) + (n(BS12['InvestmentsAndAdvances']) + n(BS12['DeferredCosts']) + n(BS12['NetLoan']) + n(BS12['DefinedPensionBenefit']) + n(BS12['FinancialAssets']))) * ((n(inflationTableValue)) / n(inflationTableAge)))
          + (n(inflationTableValue)) / n(inflationTableAge) + (n(totalCurrentAssets) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable']) + n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + n(otherMiscLiabilities)))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetInvestedCapitalExcl: (((n(BS12['NetPPE']) + n(BS12['NonCurrentDeferredAssets']) + n(otherNonCurrentAssets) + n(investmentsAndAdvances)) * (n(inflationTableValue)) / n(inflationTableAge)) + (n(totalCurrentAssets) - (n(BS12['AccountsPayable']) + n(BS12['DividendsPayable']) + n(BS12['CurrentNotesPayable'])
          + n(BS12['CurrentAccruedExpenses']) + n(BS12['CurrentDeferredLiabilities']) + n(BS12['TotalTaxPayable']) + n(BS12['OtherCurrentLiabilities']) + n(otherMiscLiabilities)))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          InflAdjNetInvestedCapitalExclGrw: netInvestedCapitalGrw === null ? "0" : (netInvestedCapitalGrw).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          plowback: n(plowbackRatio).toFixed(1) + "%",
          price: n(prices[priceDate]).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          marketCap: (n(ER12['BasicAverageShares'] / 1000000) * n(prices[priceDate])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          enterpriseValue: ((n(ER12['BasicAverageShares'] / 1000000) * n(prices[priceDate])) + (n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) +  n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']) + n(nonCurrentMiscLiabilities) + n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + n(BS12['PreferredStockEquity']) - n(BS12['NonCurrentDeferredLiabilities']) - n(BS12['NonCurrentAccruedExpenses']))).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          evOverEbitda: (((n(ER12['BasicAverageShares'] / 1000000) * n(prices[priceDate])) + (n(BS12['CurrentDebtAndCapitalLeaseObligation']) + n(BS12['LongTermDebt']) + n(BS12['LongTermCapitalLeaseObligation']) + n(BS12['NonCurrentDeferredLiabilities']) + n(BS12['NonCurrentAccruedExpenses']) +  n(BS12['DerivativeProductLiabilities']) + n(BS12['EmployeeBenefits']) + n(BS12['LongTermProvisions']) + n(BS12['RegulatoryLiabilities']) + n(BS12['OtherNonCurrentLiabilities']) + n(nonCurrentMiscLiabilities) + n(BS12['MinorityInterest']) + n(BS12['PreferredStockOfSubsidiary']) + n(BS12['PreferredSecuritiesOutsideStockEquity']) + n(BS12['PreferredStockEquity']) - n(BS12['NonCurrentDeferredLiabilities']) - n(BS12['NonCurrentAccruedExpenses']))) / (ebitda)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "x",


        //Asset Life
          nonCurrentAssetsLife: (n(otherNonCurrentAssets) + n(investmentsAndAdvances)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          depreciatingAssetLife: (n(grossFixedAssets) + n(otherNonCurrentAssets) + n(investmentsAndAdvances) + n(BS12['Inventory'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          data: 1,
          assetLifeAnnual: Math.min(n(assetLife), 30).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLife3Year: (averageAssetLife).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLifeExclIntangible: (Math.min(30, averageAssetLife)).toFixed(0),
          grossFixedAssetsInclIntangible: (grossFixedAssets + n(BS12['Goodwill']) + n(otherIntangibleAssets)).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          depreciatingAssetLifeInclIntangible: (n(grossFixedAssets) + n(BS12['Goodwill']) + n(otherIntangibleAssets) + (n(otherNonCurrentAssets) + n(investmentsAndAdvances)) + n(BS12['Inventory'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLifeAnnualInclIntangible: Math.min(n(assetLifeInclIntangibles), 30).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLife3YearInclIntangible: (averageAssetLifeInclIntangible).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          assetLifeInclIntangible: (Math.min(30, averageAssetLifeInclIntangible)).toFixed(0),
        //Salvage Value
          totalCurrentLiabilitiesMinusShortTermDebt: (n(totalCurrentLiabilities) - n(BS12['CurrentDebtAndCapitalLeaseObligation'])).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          salvageValue: (salvageValue).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
          salvageValuePercent: ((salvageValue / grossInvestedCapitalExcl) * 100).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
        
        //CFIRR
          CFIRRInclTFQ : (cfirrTFQIncl * 100).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",
          CFIRRExclTFQ : (cfirrTFQExcl * 100).toFixed(2).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%",

        // Dividend
        cashFlowPerShare: (cashFlowFromOps - (0.72 * n(IS12['InterestExpense']))) / n(ER12['BasicAverageShares'] / 1000000)
    });
  }


  return rows;
}

